// The supplier-month payment lifecycle, client side.
//
// PAIRED WITH server/lib/paymentLifecycle.js. This file holds the enum
// literals and the tab-membership predicates and NOTHING of the formula: the
// server resolves `state` / `label` / `warning` / `hint` and the client renders
// what it is given. A second copy of the precedence is how the badge on one
// screen starts disagreeing with the cell on another (that is exactly how the
// three copies of the old paid/partial rule came to exist).
import { formatDateShort } from './dateFormat';
import { isCreditNoteDoc } from './docTypes';

export const LIFECYCLE = Object.freeze({
  PAID:         'שולם',
  PARTIAL:      'שולם חלקית',
  APPROVED:     'מאושר לתשלום',
  AWAITING:     'ממתין לאישור',
  NEEDS_REVIEW: 'דורש בדיקה',
});

// /suppliers/paid shows what has money against it; /suppliers (לתשלום) shows
// everything that is not fully paid — null included. שולם חלקית is on both.
export const isPaidTabState = (state) => state === LIFECYCLE.PAID || state === LIFECYCLE.PARTIAL;
export const isOpenTabState = (state) => state !== LIFECYCLE.PAID;

// The states a row on /suppliers can be in — the filter offers exactly these.
export const LIFECYCLE_FILTER_OPTIONS = Object.freeze([
  LIFECYCLE.NEEDS_REVIEW,
  LIFECYCLE.AWAITING,
  LIFECYCLE.APPROVED,
  LIFECYCLE.PARTIAL,
]);

// Badge colours reuse the existing .badge-* classes (index.css). Green means
// money moved, blue means the restaurant said yes, grey means waiting on a
// person, red means a document needs attention first.
const BADGE_CLASS = {
  [LIFECYCLE.PAID]:         'badge-green',
  [LIFECYCLE.PARTIAL]:      'badge-green',
  [LIFECYCLE.APPROVED]:     'badge-blue',
  [LIFECYCLE.AWAITING]:     'badge-gray',
  [LIFECYCLE.NEEDS_REVIEW]: 'badge-red',
};
export const lifecycleBadgeClass = (state) => BADGE_CLASS[state] || 'badge-gray';

// ── Approve-while-paying ─────────────────────────────────────────────────────
// The payment modals offer "אשר לתשלום לחודש MM/YYYY" (or לחודשים … for a
// wider window) when what is being paid is not yet approved. These helpers
// decide that from data the page already holds — no request. Client-only (not
// part of the server pair): the server re-checks everything when
// `approve_month: true` arrives, and approves each month of the window that
// holds a document.
const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const lastDayOf = (y, m) => new Date(Number(y), Number(m), 0).getDate();

// 'MM/YYYY' when [from, to] is exactly one calendar month, else ''.
export const monthLabelFromRange = (from, to) => {
  const f = ISO_DAY_RE.exec(String(from || ''));
  const t = ISO_DAY_RE.exec(String(to || ''));
  if (!f || !t || f[1] !== t[1] || f[2] !== t[2] || f[3] !== '01') return '';
  if (Number(t[3]) !== lastDayOf(t[1], t[2])) return '';
  return `${f[2]}/${f[1]}`;
};

// How a person names the window: 'MM/YYYY' for one calendar month,
// 'MM/YYYY – MM/YYYY' for anything wider, '' when either bound is missing.
// `multi` tells the modal whether to say לחודש or לחודשים.
export const rangeLabel = (from, to) => {
  const single = monthLabelFromRange(from, to);
  if (single) return { label: single, multi: false };
  const f = ISO_DAY_RE.exec(String(from || ''));
  const t = ISO_DAY_RE.exec(String(to || ''));
  if (!f || !t) return { label: '', multi: false };
  const a = `${f[2]}/${f[1]}`;
  const b = `${t[2]}/${t[1]}`;
  return a === b ? { label: a, multi: false } : { label: `${a} – ${b}`, multi: true };
};

// The ISO single-month range a document's DD/MM/YYYY date falls in — what
// GET /suppliers/:id?date_from&date_to and POST /payments need to name the
// month. null when the date is not DD/MM/YYYY.
export const monthRangeFromDocDate = (date) => {
  const m = /^\d{2}\/(\d{2})\/(\d{4})$/.exec(String(date || ''));
  if (!m) return null;
  const last = String(lastDayOf(m[2], m[1])).padStart(2, '0');
  return { dateFrom: `${m[2]}-${m[1]}-01`, dateTo: `${m[2]}-${m[1]}-${last}` };
};

// 'MM/YYYY' from a document's DD/MM/YYYY date, else ''.
export const monthLabelFromDocDate = (date) => {
  const m = /^\d{2}\/(\d{2})\/(\d{4})$/.exec(String(date || ''));
  return m ? `${m[1]}/${m[2]}` : '';
};

// Supplier-level modal: both bounds must be known, and the window must not be
// fully approved already (approved_by is set only for a complete approval — a
// partly approved window is offered, and the server completes it). `lifecycle`
// is the row's (or the header's) lifecycle for the SAME range; when it is
// missing the state is unknown and nothing is offered — the checkbox must
// never promise an approval it cannot describe.
export const approvalOfferForRange = ({ lifecycle, dateFrom, dateTo }) => {
  const { label, multi } = rangeLabel(dateFrom, dateTo);
  if (!label || !lifecycle || lifecycle.approved_by) return null;
  return { monthLabel: label, multi };
};

// Per-document modal: the document's own month is what gets approved, so the
// offer is made only when the page's lifecycle describes that exact month —
// on a wider window the page cannot tell whether THAT month is approved, and
// the checkbox must not guess.
//
// Never offered for a חשבונית זיכוי (or any invoice in minus): "אשר לתשלום לחודש" approves a month of
// invoices, and recording a credit must not grant a supplier_approvals row (and,
// for a manager, a supplier comment) for a month that may hold no invoice at
// all. The server ignores approve_month for one too (routes/payments.js).
export const approvalOfferForDoc = ({ lifecycle, docDate, docType, docTotal, dateFrom, dateTo }) => {
  if (isCreditNoteDoc({ doc_type: docType, total: docTotal })) return null;
  const docMonth = monthLabelFromDocDate(docDate);
  if (!docMonth || docMonth !== monthLabelFromRange(dateFrom, dateTo)) return null;
  if (!lifecycle || lifecycle.approved_by) return null;
  return { monthLabel: docMonth, multi: false };
};

// 'YYYY-MM-DD HH:MM:SS' (SQLite CURRENT_TIMESTAMP, UTC) → 'dd/mm/yy' in the
// viewer's local day — the same rule every other timestamp in the app uses
// (dateFormat.js). Slicing the raw string showed the UTC day, one day early
// around midnight.
export const fmtApprovedAt = (approvedAt) =>
  /^\d{4}-\d{2}-\d{2}/.test(String(approvedAt || '')) ? (formatDateShort(approvedAt) || '') : '';
